import React, {useEffect, useMemo, useState} from 'react'
import dayjs from 'dayjs'
import {useNavigate, useLocation} from 'react-router-dom'
import {toast} from 'react-toastify'
import {useDispatch} from 'react-redux'
import {addDays} from 'date-fns'
import rawCountryStates from 'iso3166-2-db/i18n/en'
import {Modal, ModalTitle} from '@thryvlabs/maverick'
import {
  setEmailLookupState,
  setCountrySelectState,
  goNext,
  resetJourney,
} from '../../app-store/AccountSetupSlice'
import {fetchAccounts} from '../../app-store/accounts/accountsThunk'
import {useUserFeatures} from '../../context/user-features-context'
import {iso3toiso2} from '../../utils/iso3-to-iso2'
import {isSelectableCountry} from '../../utils/selectable-countries'
import {DateTimeModal} from '../../components/date-time-modal.js'
import {useAuthClient} from '../../utils/use-auth-client'
import {useAsync} from '../../utils/use-async'
import {dateTimeConverter} from '../../utils/timezones'
import {useUserRoles} from '../../utils/use-user-roles'
import {AUTHORIZED_ROLES_BY_SCREENS} from '../../utils/constants'
import {getAuthByRole} from '../../utils/get-screen-auth-by-role'
import NotFoundPage from '../../components/not-found-page'
import {DetailsModal} from '../../components/details-modal'
import styled from '@emotion/styled'
import {
  ToggleWrapper,
  ToggleTab,
} from '../../accountManagement/shared/Tabs/Tabs.styles'
import {
  ContentArea,
  MainArea,
  FormField,
  FormLabel,
  FormInput,
} from '../ProposalTool/features/Dashboard/styled.components'
import {theme} from '../ProposalTool/features/Dashboard/theme'
import {
  TableWrapper,
  StyledTable,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  EmptyStateTitle,
} from '../ProposalTool/components/ProposalTable/styled.components'
import {ProposalPagination} from '../ProposalTool/components/ProposalPagination'
import {Toolbar, Filters, Badge} from './AppointmentsRedesign.styles'

const FormSelect = styled.select`
  padding: 8px 32px 8px 12px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  font-size: 14px;
  font-family: ${theme.font.family};
  color: ${theme.colors.text};
  background: #fff
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23888' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E")
    no-repeat right 10px center;
  appearance: none;
  outline: none;
  cursor: pointer;
  transition: border-color ${theme.transition.fast};

  &:focus {
    border-color: ${theme.colors.primary};
  }
`

const tableButtonStyles = `
  display: inline-flex !important;
  align-items: center !important;
  padding: 3px 10px !important;
  border-radius: ${theme.radius.full} !important;
  border: 1px solid ${theme.colors.border} !important;
  background: #f4f6fb !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  font-family: ${theme.font.family} !important;
  color: ${theme.colors.text} !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  box-shadow: none !important;
  transition: background ${theme.transition.fast} !important;
`

const TableBtn = styled.button`
  ${tableButtonStyles}

  &:hover:not(:disabled) {
    background: ${theme.colors.hover} !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ActionsCell = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;

  & button:not([class*='modal'] button):not([class*='Modal'] button) {
    ${tableButtonStyles}
  }

  &
    button:not([class*='modal'] button):not([class*='Modal'] button):hover:not(
      :disabled
    ) {
    background: ${theme.colors.hover} !important;
  }

  & button:not([class*='modal'] button):not([class*='Modal'] button):disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

// NOTE: Standalone redesign of the Appointments page (FULSAS-14636 /
// BUSREQ-12959). It does not modify the legacy DemoAppointments component, so
// it can be swapped in later by pointing the /appointments route at it.

const TABS = [
  {key: 'onboarding', label: 'Onboarding', endpoint: 'appointments'},
  {key: 'demo', label: 'Demo Requests', endpoint: 'demos'},
]

// Same list the legacy DemoAppointments uses. Default is Central (CDT).
const TIME_ZONES = [
  {name: "Customer's Time Zone", value: 'customer'},
  {name: 'Hawaii', value: 'Pacific/Honolulu'},
  {name: 'Alaska', value: 'America/Juneau'},
  {name: 'Pacific Time (US & Canada)', value: 'America/Los_Angeles'},
  {name: 'Arizona Time', value: 'America/Phoenix'},
  {name: 'Mountain Time (US & Canada)', value: 'America/Denver'},
  {name: 'Central Time (US & Canada)', value: 'America/Chicago'},
  {name: 'Eastern Time (US & Canada)', value: 'America/New_York'},
  {name: 'Eastern Time (Cayman)', value: 'America/Cayman'},
  {name: 'Atlantic Time (Canada)', value: 'America/Halifax'},
  {name: 'Atlantic Time (Barbados)', value: 'America/Barbados'},
  {name: 'Perth', value: 'Australia/Perth'},
  {name: 'Adelaide', value: 'Australia/Adelaide'},
  {name: 'Darwin', value: 'Australia/Darwin'},
  {name: 'Brisbane', value: 'Australia/Queensland'},
  {name: 'Sydney', value: 'Australia/Sydney'},
  {name: 'Melbourne', value: 'Australia/Melbourne'},
  {name: 'Hobart', value: 'Australia/Hobart'},
  {name: 'Wellington', value: 'Pacific/Auckland'},
  {name: 'Auckland', value: 'Pacific/Auckland'},
  {name: 'Christchurch', value: 'Pacific/Auckland'},
  {name: 'Dunedin', value: 'Pacific/Auckland'},
  {name: 'Chatham Islands', value: 'Pacific/Chatham'},
]

const DEFAULT_TIMEZONE = TIME_ZONES.find(z => z.value === 'America/Chicago')

const TIME_OPTIONS = ['All', 'Today', 'Tomorrow', 'Upcoming', 'Past'].map(
  v => ({name: v, value: v}),
)

// Same titles the legacy DemoAppointments uses for the Details modal.
const modalTitles = {
  demo: 'Thryv Demo Request',
  onboarding: 'Thryv Onboarding Appointment',
  'creative consultation': 'Thryv Creative Consultation Appointment',
  'thryv-success-services': 'Thryv Success Services Appointment',
  'thryv success services': 'Thryv Success Services Appointment',
  creative: 'Thryv Creative Consultation Appointment',
  alignment: 'Thryv Alignment Call',
  keapDemo: 'Keap Demo Request',
}

const demoModalTitles = {
  Thryv: 'Thryv Demo Request',
  'Business Center': 'Business Center Demo Request',
  'Marketing Center': 'Marketing Center Demo Request',
  'Keap Demo Request': 'Keap Demo Request',
  HIPAA: 'HIPAA Demo Request',
  'Restricted Access': 'Restricted Access Demo Request',
  'Listing Management': 'Listing Management Demo Request - United States',
}

const isValidUrl = urlString => {
  try {
    return Boolean(new URL(urlString))
  } catch (e) {
    return false
  }
}

const LIST_OF_COUNTRIES = Object.entries(rawCountryStates)
  .filter(country => isSelectableCountry(country[1].iso))
  .map(country => ({name: country[1].name, value: country[1].iso}))
  .sort((a, b) => (a.name > b.name ? 1 : -1))

const getState = (countryIso, stateIso) => {
  const stateData = rawCountryStates[countryIso].regions.find(
    s => s.iso === stateIso,
  )
  return {name: stateData.name, value: stateData.iso || stateData.name}
}

const formatZoomUrl = zoomUrl => {
  if (!zoomUrl) return zoomUrl
  if (zoomUrl.indexOf('?pwd') === -1 && zoomUrl.indexOf('pwd') !== -1) {
    const pos = zoomUrl.indexOf('pwd')
    return zoomUrl.slice(0, pos) + '?pwd' + zoomUrl.slice(pos + 3)
  }
  return zoomUrl
}

function CopyButton({text}) {
  const [copied, setCopied] = React.useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      // clipboard unavailable
    }
  }
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <button
        type="button"
        onClick={handleCopy}
        title="Copy link"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px',
          flexShrink: 0,
          color: copied ? '#22c55e' : '#64748b',
          display: 'inline-flex',
          alignItems: 'center',
          transition: 'color 0.2s ease',
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          style={{transition: 'opacity 0.2s ease'}}
        >
          {copied ? (
            <path
              d="m5 12.5 4.5 4.5L19 7.5"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ) : (
            <>
              <rect
                x="9"
                y="9"
                width="11"
                height="11"
                rx="2.2"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <path
                d="M5 15V6.2A2.2 2.2 0 0 1 7.2 4H16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </>
          )}
        </svg>
      </button>
      <span
        style={{
          fontSize: '11px',
          color: '#22c55e',
          fontWeight: 500,
          opacity: copied ? 1 : 0,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
        }}
      >
        Copied!
      </span>
    </div>
  )
}

// "All" sends no date range (same shape the legacy page uses for each range).
const getTimeQuery = value => {
  const now = dayjs().unix()
  const day = d => dayjs().add(d, 'day')
  switch (value) {
    case 'Today':
      return `start_date=${dayjs().startOf('date').unix()}&end_date=${dayjs()
        .endOf('date')
        .unix()}`
    case 'Tomorrow':
      return `start_date=${day(1).startOf('date').unix()}&end_date=${day(1)
        .endOf('date')
        .unix()}`
    case 'Upcoming':
      return `start_date=${now}`
    case 'Past':
      return `end_date=${now}`
    default:
      return ''
  }
}

function AppointmentsRedesign() {
  const {roles} = useUserRoles()
  const client = useAuthClient()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const {Orders_Menu_Access: hasOrdersLinkAccess} = useUserFeatures()
  const {data, run, isLoading} = useAsync({status: 'pending'})
  const [addressNeedsToBeUpdated, setAddressNeedsToBeUpdated] = useState(false)
  const [loadingNewOrderId, setLoadingNewOrderId] = useState(null)

  const [activeTab, setActiveTab] = useState(
    location.state?.activeTab ?? 'onboarding',
  )
  const [time, setTime] = useState('Upcoming')
  const [sortAsc, setSortAsc] = useState(true)
  const [term, setTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [cancelledIds, setCancelledIds] = useState([])
  const [searchInput, setSearchInput] = useState('')

  const tab = TABS.find(t => t.key === activeTab)
  const [timeZoneName, setTimeZoneName] = useState(DEFAULT_TIMEZONE.name)
  const timeZone =
    TIME_ZONES.find(z => z.name === timeZoneName) || DEFAULT_TIMEZONE

  useEffect(() => {
    const parts = [getTimeQuery(time), term ? `term=${term}` : ''].filter(
      Boolean,
    )
    run(client(`${tab.endpoint}?${parts.join('&')}`))
    setPage(1)
    setSortAsc(time !== 'Past')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, time, term])

  const rows = useMemo(() => {
    const list = Array.isArray(data) ? [...data] : []
    // Sort on the Date/Time value (scheduled_utc); rows without a valid date
    // always go last so they can't break the ordering.
    const ts = row => {
      const v = row.scheduled_utc ? dayjs(row.scheduled_utc).valueOf() : NaN
      return Number.isNaN(v) ? null : v
    }
    list.sort((a, b) => {
      const ta = ts(a)
      const tb = ts(b)
      if (ta === null && tb === null) return 0
      if (ta === null) return 1
      if (tb === null) return -1
      return sortAsc ? ta - tb : tb - ta
    })
    return list
  }, [data, sortAsc])

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize)

  if (!getAuthByRole(AUTHORIZED_ROLES_BY_SCREENS.appointments, roles)) {
    return <NotFoundPage />
  }

  const when = row =>
    dateTimeConverter(
      row.scheduled_utc,
      row.demo_timezone,
      'datetime',
      row.demo_datetime,
      timeZone,
    )

  const getDetails = async id => {
    const [r = {}] = (await client(`${tab.endpoint}/${id}`)) || []
    return buildDetails(r)
  }

  const buildDetails = r => {
    const name = [r.cust_first_name, r.cust_last_name].filter(Boolean).join(' ')
    const city = r.cust_city ? `, ${r.cust_city}` : ''
    const state = r.cust_state ? `, ${r.cust_state}` : ''
    const country = r.cust_country ? `, ${r.cust_country}` : ''
    const fullAddress = `${r.cust_address}${city}${state}${country}`
    return [
      {
        sectionType: 'object',
        sectionName: '',
        sectionData: {
          Date: dateTimeConverter(
            r.scheduled_utc,
            r.demo_timezone,
            'date',
            r.demo_datetime,
            timeZone,
          ),
          Time: dateTimeConverter(
            r.scheduled_utc,
            r.demo_timezone,
            'time',
            r.demo_datetime,
            timeZone,
          ),
          'Sales Rep Name':
            [r.rep_first_name, r.rep_last_name].filter(Boolean).join(' ') ||
            '-',
          'Sales Rep Phone': r.rep_phone || '-',
          'Sales Rep Email': r.rep_email || '-',
        },
      },
      {
        sectionType: 'object',
        sectionName: 'Customer Information',
        sectionData: {
          Company: r.cust_company || '-',
          Address: fullAddress || '-',
          Name: name || '-',
          Phone: r.cust_phone || '-',
          Email: r.cust_email || '-',
          Industry: r.industry_name || '-',
          Website: r.cust_website || '-',
          'Google Map': r.cust_url || '-',
        },
      },
      {
        sectionType: 'object',
        sectionName: 'Discovery',
        sectionData: {
          'Business Opportunity': r.cust_problem || '-',
          'Current Software Stack': r.current_software || '-',
          'Timeline & Buying Intent': r.cust_intents || '-',
        },
      },
      {
        sectionType: 'custom',
        sectionName: 'Notes',
        Component: () => (
          <div className="font-normal text-p-base font-open-sans text-thryv-black-600">
            <p>
              Were you able to complete the &apos;customer journey&apos; with
              this client? <strong>{r.cust_journey}</strong>
            </p>
            <p>
              Are all decision makers going to be present for this meeting?{' '}
              <strong>{r.cust_decision}</strong>
            </p>
          </div>
        ),
      },
      {
        sectionType: 'custom',
        sectionName: 'Demo Presenter',
        Component: () => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '8px 0',
            }}
          >
            {r.demo_presenter_img ? (
              <img
                src={r.demo_presenter_img}
                alt={r.demo_presenter}
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  background: '#e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '28px',
                  color: '#64748b',
                  fontWeight: 700,
                }}
              >
                {(r.demo_presenter || '?')[0].toUpperCase()}
              </div>
            )}
            <div style={{minWidth: 0, flex: 1, overflow: 'hidden'}}>
              <p
                style={{
                  margin: '0 0 6px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1e293b',
                }}
              >
                {r.demo_presenter}
              </p>
              {isValidUrl(r.demo_url) ? (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '4px',
                    }}
                  >
                    <a
                      href={formatZoomUrl(r.demo_url)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: '13px',
                        wordBreak: 'break-all',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {formatZoomUrl(r.demo_url)}
                    </a>
                    <CopyButton text={formatZoomUrl(r.demo_url)} />
                  </div>
                </div>
              ) : (
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#64748b',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {formatZoomUrl(r.demo_url)}
                </p>
              )}
            </div>
          </div>
        ),
      },
    ]
  }

  const checkAddressInfo = (address, city, state, country) => {
    if (!address || !city || !state || !country) {
      setAddressNeedsToBeUpdated(true)
    }
  }

  const scheduleFollowup = async apptData => {
    try {
      const [demoDetails] = await client(
        `${tab.endpoint}/${apptData.originalApptId}`,
      )
      demoDetails.cust_country =
        demoDetails.cust_country.length === 2
          ? demoDetails.cust_country
          : iso3toiso2[demoDetails.cust_country.toUpperCase()]
      const u = apptData.updatedFields
      const followupData = {
        contactNumber: demoDetails.cust_phone,
        demoConductedInSpanish: demoDetails.demoLanguage === 'Spanish',
        prospectsCity: u.prospectsCity || demoDetails.cust_city,
        prospectsCompany: demoDetails.cust_company,
        prospectsCountry:
          u.prospectsCountry ||
          LIST_OF_COUNTRIES.find(c => c.value === demoDetails.cust_country),
        prospectsDate: apptData.longDate,
        prospectsEmailAddress: demoDetails.cust_email,
        prospectsFirstName: demoDetails.cust_first_name,
        prospectsLastName: demoDetails.cust_last_name,
        prospectsMobilePhone: demoDetails.cust_phone,
        prospectsPlaceURL: demoDetails.cust_url || '',
        prospectsPostalCode: demoDetails.cust_zip,
        prospectsState:
          u.prospectsState ||
          getState(demoDetails.cust_country, demoDetails.cust_state),
        prospectsStreet: u.streetAddress || demoDetails.cust_address,
        scheduledDateTime: apptData.ISODate,
        staffId: apptData.selectedStaffId,
        thryvBookingDate: apptData.startDateTime,
        timeZoneId: apptData.timeZoneId,
        timeZoneName: apptData.timeZoneName,
        serviceId: apptData.selectedServiceId,
        isFollowup: true,
      }
      await run(client('demos/', {data: followupData}))
      toast.success('Follow up scheduled!')
      setTime('Upcoming')
    } catch (responseError) {
      if (responseError.status !== 400) {
        throw responseError
      }
      if (responseError?.message?.includes('select a new date and time')) {
        toast.error(responseError.message, {
          autoClose: false,
          closeOnClick: true,
          theme: 'colored',
        })
      } else {
        toast.error(responseError.message)
      }
    }
  }

  const handleNewOrderClick = async (demoId, custCountry) => {
    setLoadingNewOrderId(demoId)
    try {
      const [details] = await client(`${tab.endpoint}/${demoId}`)
      const email = details.cust_email
      const validation = await client(`orders/validate-email/${email}`)
      if (validation.orderCanContinue) {
        const isoValue = iso3toiso2[custCountry?.toUpperCase()] || custCountry
        dispatch(resetJourney())
        dispatch(
          setEmailLookupState({email, accountType: 'new-customer', meta: {}}),
        )
        dispatch(setCountrySelectState(isoValue))
        dispatch(goNext())
        navigate('/accounts/account-setup', {
          state: {
            prefillFromDemo: {
              businessName: details.cust_company,
              firstName: details.cust_first_name,
              lastName: details.cust_last_name,
              mobile: details.cust_phone,
              telephone: details.cust_phone,
              address: details.cust_address,
              city: details.cust_city,
              state: details.cust_state,
              postCode: details.cust_zip,
            },
          },
        })
      } else {
        await dispatch(fetchAccounts({client, email}))
        navigate('/accounts/all-accounts', {
          state: {context: 'emailLookupReady', email},
        })
      }
    } catch (err) {
      toast.error('Unable to process new order request. Please try again.')
    } finally {
      setLoadingNewOrderId(null)
    }
  }

  const cancel = async id => {
    try {
      await client(`demos/${id}`, {method: 'DELETE'})
      setCancelledIds(ids => [...ids, id])
      toast.success('Appointment cancelled successfully')
      await client('ordercapture/services/updateScheduledAppointmentStatus', {
        data: {schedule_status: 'Cancelled', appt_id: id},
        method: 'PUT',
      })
    } catch (e) {
      toast.warn('There was a problem with the cancellation')
    }
  }

  const addToCalendar = async (dateUTC, id) => {
    const [r = {}] = (await client(`${tab.endpoint}/${id}`)) || []
    const start = dayjs(dateUTC)
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//hacksw/handcal//NONSGML v1.0//EN',
      'BEGIN:VEVENT',
      'UID:' + start.format('YYYYMMDDTHHmmss'),
      'DTSTAMP:' + dayjs().format('YYYYMMDDTHHmmss'),
      'DTSTART:' + start.format('YYYYMMDDTHHmmss'),
      'DTEND:' + start.add(30, 'minute').format('YYYYMMDDTHHmmss'),
      'SUMMARY:Demo',
      'LOCATION:See Invite Details for Zoom Link',
      `DESCRIPTION:Demo for ${r.cust_first_name} ${r.cust_last_name} at ${
        r.cust_company
      }. Link: ${formatZoomUrl(r.demo_url)}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\n')
    const a = document.createElement('a')
    const url = window.URL.createObjectURL(
      new Blob([ics], {type: 'text/calendar;charset=utf-8'}),
    )
    a.href = url
    a.download = 'thryv-event.ics'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const renderRow = row => {
    const cancelled =
      /cancelled/i.test(row.alert_type || '') ||
      cancelledIds.includes(row.demo_appt_id)
    const isPast = row.scheduled_utc
      ? dayjs(row.scheduled_utc).isBefore(dayjs())
      : false
    const company =
      row.demo_source === 'Marketplace'
        ? `${row.cust_first_name} ${row.cust_last_name}`
        : row.cust_company || '-'
    return (
      <TR key={row.id}>
        <TD>
          {company}
          {row.meeting_type === 'alignment' ? ' - Alignment Call' : ''}
        </TD>
        <TD>{row.demo_presenter || '-'}</TD>
        <TD>{when(row)}</TD>
        <TD>
          <ActionsCell>
            <DetailsModal
              getData={() => getDetails(row.demo_appt_id)}
              title={
                row.meeting_type === 'demo'
                  ? demoModalTitles[row.product]
                  : modalTitles[row.meeting_type]
              }
              buttonName="Details"
              meeting_type={row.meeting_type}
              selectedTimeZone={timeZone}
              height="40em"
            />
            {!cancelled &&
              (['Today', 'Tomorrow', 'Upcoming'].includes(time) ||
                (time === 'All' && !isPast)) && (
                <>
                  <TableBtn
                    type="button"
                    onClick={() =>
                      addToCalendar(row.scheduled_utc, row.demo_appt_id)
                    }
                  >
                    Add To Calendar
                  </TableBtn>
                  <Modal
                    variant="default"
                    title="Cancel Appointment"
                    btnText="Cancel"
                    btnType="secondary"
                    actionClose
                    footer
                    action
                    btnActionText="Confirm"
                    btnAction={() => cancel(row.demo_appt_id)}
                    primaryFooterVariant="dark"
                  >
                    <ModalTitle variant="subtitle" size="md">
                      Please confirm Appointment cancellation.
                    </ModalTitle>
                  </Modal>
                </>
              )}
            {!cancelled &&
              (time === 'Today' ||
                time === 'Upcoming' ||
                (time === 'All' && !isPast)) && (
                <TableBtn
                  type="button"
                  onClick={() =>
                    window.open(formatZoomUrl(row.demo_url), '_blank')
                  }
                >
                  Join Now
                </TableBtn>
              )}
            {!cancelled &&
              row.meeting_type === 'demo' &&
              (time === 'Past' || (time === 'All' && isPast)) && (
                <>
                  <DateTimeModal
                    modalTitle="Schedule Follow-up Demo"
                    buttonText="Schedule Follow-up"
                    modalId="new-demo-request-modal"
                    isFollowup
                    saveDateTime={scheduleFollowup}
                    availableTimeOptions={{
                      prospectsCountry: row.cust_country,
                      prospectsCity: row.cust_city,
                      prospectsState: row.cust_state,
                      staffId: row.demo_staff_id,
                      originalApptId: row.demo_appt_id,
                      streetAddress: row.cust_address,
                    }}
                    updateAddress={addressNeedsToBeUpdated}
                    onOpen={() =>
                      checkAddressInfo(
                        row.cust_address,
                        row.cust_city,
                        row.cust_state,
                        row.cust_country,
                      )
                    }
                    setAddressNeedsToBeUpdated={setAddressNeedsToBeUpdated}
                    minDate={new Date()}
                    maxDate={addDays(new Date(), 7)}
                  />
                  {hasOrdersLinkAccess && (
                    <TableBtn
                      type="button"
                      onClick={() =>
                        handleNewOrderClick(row.demo_appt_id, row.cust_country)
                      }
                      disabled={loadingNewOrderId !== null}
                    >
                      New Order
                    </TableBtn>
                  )}
                </>
              )}
            {cancelled && <Badge status="cancelled">Cancelled</Badge>}
            {!cancelled &&
              (['Tomorrow', 'Upcoming'].includes(time) ||
                (time === 'All' && !isPast)) && (
                <Badge status="upcoming">Upcoming</Badge>
              )}
          </ActionsCell>
        </TD>
      </TR>
    )
  }

  return (
    <ContentArea style={{padding: '12px 16px'}}>
      <MainArea style={{fontSize: 'clamp(11px, 1vw, 13px)'}}>
        <Toolbar>
          <FormField>
            <FormLabel>Appointment Type</FormLabel>
            <ToggleWrapper role="tablist" aria-label="Appointment type">
              {TABS.map(t => (
                <ToggleTab
                  key={t.key}
                  role="tab"
                  aria-selected={t.key === activeTab}
                  isActive={t.key === activeTab}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </ToggleTab>
              ))}
            </ToggleWrapper>
          </FormField>
          <Filters>
            <FormField>
              <FormLabel>Time</FormLabel>
              <FormSelect value={time} onChange={e => setTime(e.target.value)}>
                {TIME_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.name}
                  </option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Time Zone</FormLabel>
              <FormSelect
                value={timeZoneName}
                onChange={e => setTimeZoneName(e.target.value)}
              >
                {TIME_ZONES.map(z => (
                  <option key={z.name} value={z.name}>
                    {z.name}
                  </option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Search</FormLabel>
              <FormInput
                type="text"
                placeholder={`Search ${tab.label}`}
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value)
                  setTerm(e.target.value.trim())
                }}
              />
            </FormField>
          </Filters>
        </Toolbar>

        <TableWrapper>
          <StyledTable>
            <THead>
              <TR>
                <TH>Company</TH>
                <TH>Presenter</TH>
                <TH
                  sortable
                  style={{cursor: 'pointer'}}
                  onClick={() => setSortAsc(v => !v)}
                >
                  Date/Time {sortAsc ? '▲' : '▼'}
                </TH>
                <TH>Action</TH>
              </TR>
            </THead>
            <TBody>
              {isLoading
                ? Array.from({length: pageSize}).map((_, i) => (
                    <TR key={`sk-${i}`}>
                      {['company', 'presenter', 'datetime', 'action'].map(
                        col => (
                          <TD key={col}>
                            <div
                              style={{
                                height: '14px',
                                borderRadius: '4px',
                                background:
                                  'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                                backgroundSize: '200% 100%',
                                animation: 'skeleton-shimmer 1.5s infinite',
                                width: col === 'action' ? '120px' : '80%',
                              }}
                            />
                          </TD>
                        ),
                      )}
                    </TR>
                  ))
                : pageRows.map(renderRow)}
            </TBody>
          </StyledTable>
        </TableWrapper>

        {!isLoading && rows.length === 0 && (
          <EmptyState>
            <EmptyStateTitle>
              Sorry, we could not find any results.
            </EmptyStateTitle>
          </EmptyState>
        )}

        <ProposalPagination
          currentPage={page}
          totalItems={rows.length}
          perPage={pageSize}
          onPageChange={setPage}
          onPerPageChange={n => {
            setPageSize(n)
            setPage(1)
          }}
        />
      </MainArea>
      <style>{`
        @keyframes skeleton-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </ContentArea>
  )
}

export {AppointmentsRedesign}
